// src/pages/admin/AdminDoctorView.tsx
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  fetchDoctors,
  addDoctorSlots,
  removeDoctorSlots,   // 管理员删除接口（仍然使用这条）
  toHHMM,              // 统一时间格式（HH:mm）
  type Doctor,
} from '../../api/adminSchedules';
import api from '../../api/axios';

type AvailSlot = { time: string; status: 'available' | 'limited' | 'full' | 'none' };
type AvailDay = { date: string; slots: AvailSlot[] };

// 生成 30 分粒度时间
const generateTimeSlots = () => {
  const morning = [9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5];
  const afternoon = [13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5];
  const fmt = (n: number) =>
    `${String(Math.floor(n)).padStart(2, '0')}:${n % 1 === 0 ? '00' : '30'}`;
  return [...morning, ...afternoon].map(fmt);
};

// 针对后端“时间字符串匹配不严”的兜底：同一时间的几种变体
const buildTimeVariants = (hhmm: string): string[] => {
  // 输入已是 HH:mm
  const [hStr, mStr] = hhmm.split(':');
  const hNum = Number(hStr);
  const hNoPad = String(hNum);            // 9
  const hPad = String(hNum).padStart(2, '0'); // 09

  const base1 = `${hPad}:${mStr}`;        // 09:00
  const base2 = `${hNoPad}:${mStr}`;      // 9:00
  const sec1 = `${base1}:00`;             // 09:00:00
  const sec2 = `${base2}:00`;             // 9:00:00

  // 去重
  return Array.from(new Set([base1, base2, sec1, sec2]));
};

const AdminDoctorView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [weekOffset, setWeekOffset] = useState(0);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [doctorId, setDoctorId] = useState<number | null>(null);

  const [days, setDays] = useState<AvailDay[]>([]);
  const [loading, setLoading] = useState(false);

  const start = useMemo(
    () => dayjs().startOf('day').add(weekOffset * 7, 'day'),
    [weekOffset]
  );
  const times = useMemo(() => generateTimeSlots(), []);

  // locale
  useEffect(() => {
    dayjs.locale(i18n.language === 'zh' ? 'zh-cn' : 'ja');
  }, [i18n.language]);

  // 解析 URL ?doctorId=xx
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const id = p.get('doctorId');
    if (id) setDoctorId(Number(id));
  }, [location.search]);

  // 拉医生列表
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchDoctors();
        setDoctors(list || []);
        if (!doctorId && list?.length) setDoctorId(list[0].id);
      } catch {
        toast.error(
          t('admin.schedules.fetch_doctors_failed') || '医師一覧の取得に失敗しました'
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 拉某医生的“可预约/排班”矩阵（/availability?doctor_id=）
  const load = async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const startStr = start.format('YYYY-MM-DD');
      const { data } = await api.get<AvailDay[]>('/availability', {
        params: { start_date: startStr, days: 7, doctor_id: doctorId },
      });
      // 统一时间格式为 HH:mm
      const fixed = (data || []).map((d) => ({
        ...d,
        slots: (d.slots || []).map((s) => ({ ...s, time: toHHMM(s.time) })),
      }));
      setDays(fixed);
    } catch (e) {
      console.error(e);
      toast.error(t('admin.schedules.fetch_failed') || 'スケジュールの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, weekOffset]);

  const formatWeekday = (d: string) => {
    const di = dayjs(d).day();
    const zh = ['日', '一', '二', '三', '四', '五', '六'];
    const ja = ['日', '月', '火', '水', '木', '金', '土'];
    return i18n.language === 'zh' ? `周${zh[di]}` : ja[di];
  };

  // 颜色规则：过期灰；已预约红；出勤（limited/available）黄；未排班灰；保底绿
  const isPast = (dateStr: string, timeStr: string) => {
    const [h, m] = toHHMM(timeStr).split(':').map(Number);
    const dt = dayjs(dateStr).hour(h).minute(m).second(0);
    return dt.isBefore(dayjs());
  };
  const dotClass = (status: AvailSlot['status'], date: string, time: string) => {
    if (isPast(date, time)) return 'bg-gray-300';
    if (status === 'full') return 'bg-red-500';
    if (status === 'limited' || status === 'available') return 'bg-yellow-400';
    if (status === 'none') return 'bg-gray-200';
    return 'bg-green-400';
  };

  // 逐个变体尝试删除
  const tryDeleteOneSlot = async (doctor_id: number, date: string, hhmm: string) => {
    const variants = buildTimeVariants(hhmm);

    for (const v of variants) {
      try {
        const resp = await removeDoctorSlots({ doctor_id, date, times: [v] });

        if (resp && resp.deleted && resp.deleted > 0) {
          return true;
        }
      } catch (err) {
        console.error('[AdminDoctorView] Delete error for', v, err);
      }
    }
    return false;
  };

  // 管理员开/关“该医生”的出勤
  const onToggle = async (date: string, time: string, status: AvailSlot['status']) => {
    if (!doctorId) return;

    if (status === 'full') {
      toast.info(
        t('admin.doctor_view.has_reservation') || 'この枠には既に予約があります'
      );
      return;
    }
    if (isPast(date, time)) {
      toast.info(
        t('admin.schedules.past_slot_not_allowed') || '過去の時間は操作できません'
      );
      return;
    }

    try {
      const hhmm = toHHMM(time);

      if (status === 'none') {
        // 新增该医生在此时段的出勤
        await addDoctorSlots({ doctor_id: doctorId, date, times: [hhmm] });
        toast.success(t('admin.common.saved') || '保存しました');
      } else if (status === 'limited' || status === 'available') {
        // 删除该医生在此时段的出勤（多格式兜底）
        const ok = await tryDeleteOneSlot(doctorId, date, hhmm);
        if (!ok) {
          // 明确提示后端时间格式问题
          toast.warn(
            i18n.language === 'zh'
              ? '删除失败：后端未匹配到该时间段。很可能是时间格式不一致（例如 09:00 / 9:00 / 含秒）。请后端统一时间格式或提供“按ID删除”的接口。'
              : '削除失敗：該当枠が見つかりません。時間表記（09:00/9:00/秒付き）の不一致が原因の可能性があります。サーバー側で時間正規化、またはID指定削除の提供をご検討ください。'
          );
        } else {
          toast.success(t('admin.common.saved') || '保存しました');
        }
      } else {
        toast.info(t('admin.common.nothing_changed') || '変更はありません');
      }

      await load();
    } catch (e) {
      console.error(e);
      toast.error(t('admin.common.save_failed') || '保存に失敗しました');
    }
  };

  const doctorName = useMemo(() => {
    const d = doctors.find((x) => x.id === doctorId);
    return d ? (d.name || d.email || `Doctor#${d.id}`) : '';
  }, [doctors, doctorId]);

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* 顶栏：返回 + 医师选择 + 周切换 */}
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/schedules')}
              className="px-4 py-2 rounded border hover:bg-gray-50"
            >
              ← {t('admin.common.back') || '戻る'}
            </button>
            <div>
              <h1 className="text-2xl font-semibold">
                {t('admin.doctor_view.title') || '医師別ビュー'}
              </h1>
              <p className="text-gray-500 mt-1 text-sm">
                {t('admin.doctor_view.subtitle') || 'この医師の出勤枠を直接編集'}
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <select
              value={doctorId ?? ''}
              onChange={(e) => setDoctorId(Number(e.target.value))}
              className="border rounded px-3 py-2 bg-white"
            >
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.email || `Doctor#${d.id}`}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <button
                className="px-4 py-2 border rounded hover:bg-gray-100"
                onClick={() => setWeekOffset((p) => Math.max(0, p - 1))}
                disabled={weekOffset === 0}
              >
                {t('admin.schedules.prev_week') || '← 前の週'}
              </button>
              <button
                className="px-4 py-2 border rounded hover:bg-gray-100"
                onClick={() => setWeekOffset((p) => p + 1)}
              >
                {t('admin.schedules.next_week') || '次の週 →'}
              </button>
            </div>
          </div>
        </div>

        {/* 表格 */}
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="min-w-full border-collapse text-center">
            <thead>
              <tr>
                <th className="bg-gray-100 px-3 py-3 border sticky left-0 z-10">
                  {t('admin.schedules.time') || '時間'}
                </th>
                {days.map((d) => (
                  <th key={d.date} className="bg-gray-100 px-3 py-3 border whitespace-nowrap">
                    {dayjs(d.date).format('MM/DD')}（{formatWeekday(d.date)}）
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={1 + days.length} className="py-10 text-gray-500">
                    {t('admin.common.loading') || '読み込み中…'}
                  </td>
                </tr>
              ) : (
                times.map((tm) => (
                  <tr key={tm}>
                    <td className="bg-gray-50 border px-3 py-2 sticky left-0 z-0 font-semibold">
                      {tm}
                    </td>
                    {days.map((d) => {
                      const slot = d.slots.find((s) => toHHMM(s.time) === toHHMM(tm));
                      const status = (slot?.status || 'none') as AvailSlot['status'];
                      const disabled = status === 'full' || isPast(d.date, tm);
                      return (
                        <td key={`${d.date}-${tm}`} className="border px-2 py-2 align-middle">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <span
                              className={classNames(
                                'inline-block w-3 h-3 rounded-full',
                                dotClass(status, d.date, tm)
                              )}
                            />
                            <span className="text-xs text-gray-600">
                              {status === 'full'
                                ? t('admin.doctor_view.reserved') || '予約あり'
                                : status === 'limited' || status === 'available'
                                  ? t('admin.doctor_view.scheduled') || '出勤'
                                  : t('admin.doctor_view.not_scheduled') || '未設定'}
                            </span>
                          </div>
                          <button
                            disabled={disabled}
                            onClick={() => onToggle(d.date, tm, status)}
                            className={classNames(
                              'text-xs px-3 py-1 rounded border',
                              disabled
                                ? 'opacity-40 cursor-not-allowed'
                                : status === 'none'
                                  ? 'bg-coral/5 border-coral/30 text-coral hover:bg-coral-light'
                                  : 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                            )}
                            title={
                              disabled
                                ? status === 'full'
                                  ? (t('admin.doctor_view.cannot_remove_reserved') ||
                                    '予約があるため削除できません')
                                  : (t('admin.schedules.past_slot_not_allowed') ||
                                    '過去の時間は操作できません')
                                : status === 'none'
                                  ? (t('admin.doctor_view.add_this_slot') || 'この枠を追加')
                                  : (t('admin.doctor_view.remove_this_slot') || 'この枠を削除')
                            }
                          >
                            {status === 'none'
                              ? (t('admin.doctor_view.add') || '入れる')
                              : (t('admin.doctor_view.remove') || '外す')}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 当前医生名 */}
        {doctorId && (
          <div className="mt-4 text-sm text-gray-500">
            {t('admin.doctor_view.current_doctor') || '対象医師'}：
            <span className="font-medium">
              {doctorName}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDoctorView;
